import { UseFormReturn, useWatch } from 'react-hook-form';
import { IFieldElem } from '@models/IFormData';
import { areAllDisplayValuesEmpty, evalDisplayExpression } from '@utils/documentUtils';
import { FormulaManager, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { handlerFieldDisplayWatch } from '@utils/fieldWatchUtils';
import { EnsureExternalDataSourceLoaded, extractExternalDataSourceNames } from '@/utils/externalDataSourceUtils';
import type { IFormulaTraceTarget } from '@/context/DocumentFormulaTraceContext';
import { useFormulaTracePublisher } from './useFormulaTracePublisher';

export interface UseFormulaDisplayOptions {
    /** Фоллбэк для пустого watch (например, text?.value) */
    fallback?: string;
    /** Функция постобработки результата (например, замена \' и \\n) */
    postProcess?: (value: string) => string;
    /** Функция вызова при инициализации первого значения */
    onInit?: () => void;
    /** Выполнить JS eval после подстановки значений в displayFormat */
    evalDisplayFormat?: boolean;
    /**
     * Вычислять формулу и когда все зависимые поля пусты. По умолчанию пустые значения
     * схлопывают результат в '' без вычисления — для отображаемого значения контрола это
     * верно, а для условного выражения (`{X}=='A' ? 'A' : 'B'`) съедает ветку «иначе».
     */
    evaluateOnEmpty?: boolean;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    trace?: IFormulaTraceTarget;
}

export function useFormulaDisplay(
    displayFormat: string | undefined,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    isEdit: boolean,
    isNew: boolean,
    activated: RefObject<boolean>,
    options?: UseFormulaDisplayOptions,
) {
    const {
        fallback,
        postProcess = (v: string) => v,
        onInit,
        evalDisplayFormat = false,
        evaluateOnEmpty = false,
        ensureExternalDataSourceLoaded,
        trace,
    } = options ?? {};
    const runtime = useContext(DocumentRuntimeContext);
    const remoteDataBlocked =
        runtime.mode === 'supportPreview' && hasUnsupportedRemoteFormulaData(displayFormat, runtime.dataSource);
    const externalSourceNames = useMemo(
        () => (!isNew ? extractExternalDataSourceNames(displayFormat) : []),
        [displayFormat, isNew],
    );
    const [externalDataSourceReady, setExternalDataSourceReady] = useState(externalSourceNames.length === 0);

    const manager = useMemo(() => {
        if (!displayFormat || remoteDataBlocked) return null;
        const m = new FormulaManager(displayFormat, runtime.dataSource, runtime.requestCache);
        m.Init(fields, formMethods);
        return m;
    }, [displayFormat, fields, formMethods, remoteDataBlocked, runtime.dataSource, runtime.requestCache]);
    const { publishTrace, removeTrace } = useFormulaTracePublisher(
        displayFormat,
        fields,
        manager,
        externalSourceNames,
        trace,
    );

    const [value, setValue] = useState<string>('');
    const watchDataDisplayFormat = useRef<any[]>([]);

    const initDisplay = async () => {
        if (!manager) return;
        const values = manager.GetDefaultValues(isEdit, isNew, true);
        const coll: any[] = values.map((x) => (x === null ? '' : x));

        if (activated.current) {
            if (coll.length === 0 && fallback) {
                setValue(fallback);
                publishTrace(fallback, 'evaluated', undefined, coll);
            } else if (!evaluateOnEmpty && areAllDisplayValuesEmpty(coll)) {
                setValue('');
                publishTrace('', 'evaluated', undefined, coll);
            } else {
                const dataDisplay = await manager.ReplaceFormulaValues(isEdit, isNew, coll, !evalDisplayFormat);
                if (dataDisplay) {
                    const result = postProcess(evalDisplayFormat ? evalDisplayExpression(dataDisplay) : dataDisplay);
                    setValue(result);
                    publishTrace(result, 'evaluated', undefined, coll);
                } else {
                    setValue('');
                    publishTrace('', 'evaluated', undefined, coll);
                }
            }
            onInit && onInit();
        }
    };

    const handlerWatch = async (watch: any[]) => {
        if (!manager) return;
        const dataDisplay = await handlerFieldDisplayWatch(
            watch,
            isEdit,
            isNew,
            manager,
            evalDisplayFormat,
            evaluateOnEmpty,
        );
        if (activated.current && watchDataDisplayFormat.current === dataDisplay?.watch) {
            const result = dataDisplay?.result ?? '';
            const displayResult = postProcess(evalDisplayFormat ? evalDisplayExpression(result) : result);
            setValue(displayResult);
            publishTrace(displayResult, 'evaluated', undefined, watch);
        }
    };

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            setExternalDataSourceReady(true);
            return;
        }

        setExternalDataSourceReady(false);
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            return;
        }

        let canceled = false;
        Promise.all(externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name))).finally(() => {
            if (!canceled) {
                setExternalDataSourceReady(true);
            }
        });

        return () => {
            canceled = true;
        };
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        if (remoteDataBlocked) {
            const result = postProcess(String(fallback ?? ''));
            setValue(result);
            publishTrace(result, 'fallback', 'Session-scoped источник для серверной функции формулы не настроен.');
            onInit?.();
            return;
        }

        if (!displayFormat) {
            removeTrace();
            return;
        }

        initDisplay().catch((error: unknown) => {
            publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error));
            console.error(error);
        });
    }, [externalDataSourceReady, manager, displayFormat, isEdit, isNew, remoteDataBlocked, publishTrace, removeTrace]);

    const watchFields = useMemo(() => manager?.GetWatchFields() ?? [], [manager]);
    const watch = useWatch({
        name: watchFields,
    });

    useEffect(() => {
        if (!externalDataSourceReady) {
            return;
        }

        if (displayFormat && manager) {
            watchDataDisplayFormat.current = watch;
            void handlerWatch(watch).catch((error: unknown) => {
                publishTrace(undefined, 'error', error instanceof Error ? error.message : String(error), watch);
                console.error(error);
            });
        }
    }, [watch, isEdit, isNew, displayFormat, manager, externalDataSourceReady, publishTrace]);

    return { value, setValue, manager };
}
